import { useCallback } from 'react';

import { useDebouncedCallback as useLibraryDebouncedCallback } from 'use-debounce';

import { fg } from '@atlaskit/platform-feature-flags';

import { onTextEntered } from '../../../common/utils/quick-find/events/text-entered';
import { useDebouncedCallback } from '../../../common/utils/use-debounced-callback';
import {
	useQuickFindAnalytics,
	useQuickFindAttributes,
} from '../../../controllers/quick-find/analytics';
import { useQuickFindOpen } from '../../../controllers/store/quick-find';

const ON_TEXT_ENTERED_ANALYTICS_DEBOUNCE_TIME = 300;

export const useDebouncedOnTextEnteredAnalyticsCallback: () => import('lodash').DebouncedFunc<
	() => void
> = () => {
	const quickFindOpen = useQuickFindOpen();

	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useQuickFindAttributes();

	const fireOnTextEnteredAnalyticsCallback = useCallback(() => {
		if (!quickFindOpen) {
			return;
		}

		fireAnalyticsEvent(
			onTextEntered({
				attributes: commonAttributes,
				nonPrivacySafeAttributes,
			}),
		);
	}, [commonAttributes, fireAnalyticsEvent, nonPrivacySafeAttributes, quickFindOpen]);

	return useDebouncedCallback(
		fireOnTextEnteredAnalyticsCallback,
		ON_TEXT_ENTERED_ANALYTICS_DEBOUNCE_TIME,
	);
};

// Matches `<Link prefetch="hover">` PREFETCH_DELAY in react-resource-router.
const PREFETCH_DEBOUNCE_TIME = 225;
const PREFETCH_MIN_QUERY_LENGTH = 2;

export const useDebouncedPrefetchCallback = ({
	onPrefetch,
	getQuery,
	getUrls,
}: {
	onPrefetch: ((url: string) => void) | undefined;
	getQuery: () => string;
	getUrls: (query: string) => string[];
}): [debouncedPrefetch: () => void, cancelDebouncedPrefetch: () => void] => {
	const fire = useCallback(() => {
		if (!onPrefetch || !fg('quick_find_typing_debounce_prefetch')) {
			return;
		}
		const trimmed = getQuery().trim();
		if (trimmed.length < PREFETCH_MIN_QUERY_LENGTH) {
			return;
		}
		for (const url of getUrls(trimmed)) {
			if (url) {
				onPrefetch(url);
			}
		}
	}, [onPrefetch, getQuery, getUrls]);

	// Uses `use-debounce` rather than the package's lodash `useDebouncedCallback`
	// (used above for analytics) because it auto-cancels on unmount and is
	// controllable by jest fake timers; we expose its callback + cancel.
	const [debouncedPrefetch, cancelDebouncedPrefetch] = useLibraryDebouncedCallback(
		fire,
		PREFETCH_DEBOUNCE_TIME,
	);
	return [debouncedPrefetch, cancelDebouncedPrefetch];
};
